import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

// Falha Compras por fornecedor (Adnéia, 22/10/2026, para a reunião cross): os chamados que
// estão em Falha Compras no período do filtro, por marca do pedido, do pior para o melhor,
// com o Modal do card do Novo Atendimento e o período anterior do mesmo tamanho. Quem tinha
// chamado antes e zerou continua no fim. O "+" abre os produtos do fornecedor, do que tem
// mais chamados para o que tem menos. O "Modal combinado" é o prazo que a reunião combinou:
// só registro (o ELO segue com o da planilha) e, se for diferente, acende o alerta até alguém
// corrigir na planilha. Só a gestão preenche. Os números vêm prontos da rota da Classificação.
// `abertos` (fornecedor -> true) mora no Dashboard: a aba é redesenhada a cada mudança dele.
const dia = (iso) => {
  if (!iso) return '?';
  try { return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'America/Sao_Paulo' }); }
  catch { return '?'; }
};

const FalhaComprasFornecedor = ({ dados, abertos = {}, onAlternar, podeEditar = false, onSalvarModal }) => {
  const [editando, setEditando] = useState(null);
  const [valor, setValor] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  if (!dados) return null;
  const { total, total_anterior: totalAnterior, periodo_dias: periodo, fornecedores = [], alertas_modal: alertas = [] } = dados;
  const comAnterior = totalAnterior !== null && totalAnterior !== undefined;
  const maior = Math.max(...fornecedores.map(f => f.pct), 0) || 1;
  const colunas = comAnterior ? 7 : 6;

  const editar = (f) => {
    setEditando(f.fornecedor);
    setValor(f.modal_combinado ? String(f.modal_combinado.dias) : '');
    setErro('');
  };
  const salvar = async (marca, dias) => {
    setSalvando(true); setErro('');
    try {
      await onSalvarModal(marca, dias);
      setEditando(null);
    } catch (e) {
      setErro(e?.response?.data?.detail || 'Não gravou. Tente de novo.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Falha Compras por fornecedor</CardTitle>
        <CardDescription>
          {periodo ? `Últimos ${periodo} dias` : 'Todo o período'} · {total} chamado{total === 1 ? '' : 's'} em Falha Compras
          {comAnterior && ` (${totalAnterior} nos ${periodo} dias antes)`} · do pior para o melhor · clique no + para ver os produtos
        </CardDescription>
      </CardHeader>
      <CardContent>
        {alertas.length > 0 && (
          <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-950/30" role="status">
            <p className="font-medium text-amber-800 dark:text-amber-300">
              Modal combinado na reunião diferente do que o ELO usa: falta corrigir na planilha de fornecedores
            </p>
            <ul className="mt-1 space-y-0.5 text-amber-900 dark:text-amber-200">
              {alertas.map(a => (
                <li key={a.fornecedor}>
                  <span className="font-medium">{a.fornecedor}</span>: combinado {a.combinado} · o ELO usa {a.elo}
                  {a.elo_padrao ? ' (padrão: não achado na planilha)' : ' (da planilha)'} · desde {dia(a.em)}
                </li>
              ))}
            </ul>
          </div>
        )}
        {fornecedores.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhum chamado em Falha Compras neste período.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fornecedor</TableHead>
                  <TableHead className="text-right">Chamados</TableHead>
                  <TableHead className="min-w-[180px]" title="Parte do fornecedor nos chamados de Falha Compras do período; não compara com as vendas">% dos chamados de Falha Compras</TableHead>
                  <TableHead>Modal no ELO (dias úteis)</TableHead>
                  <TableHead className="min-w-[150px]">Modal combinado</TableHead>
                  <TableHead className="text-right">Ainda abertos</TableHead>
                  {comAnterior && <TableHead className="text-right">{periodo} dias antes</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {fornecedores.map((f, idx) => {
                  const pior = idx === 0 && !f.zerou;
                  const aberto = !!abertos[f.fornecedor];
                  const produtos = f.produtos || [];
                  const comb = f.modal_combinado;
                  return (
                    <React.Fragment key={f.fornecedor}>
                      <TableRow className={pior ? 'bg-red-50 dark:bg-red-950/30' : f.zerou ? 'text-muted-foreground' : ''}>
                        <TableCell className="font-medium">
                          <div className="flex items-center">
                            {produtos.length > 0 && onAlternar ? (
                              <button type="button" onClick={() => onAlternar(f.fornecedor)}
                                aria-expanded={aberto}
                                aria-label={`${aberto ? 'Fechar' : 'Ver'} os produtos de ${f.fornecedor}`}
                                className="mr-2 h-6 w-6 shrink-0 rounded border border-input text-sm leading-none hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                                {aberto ? '−' : '+'}
                              </button>
                            ) : <span className="mr-2 w-6 shrink-0" />}
                            <span>{f.fornecedor === '?' ? '? (pedido sem marca)' : f.fornecedor}</span>
                            {pior && <Badge variant="outline" className="ml-2 border-red-300 text-red-700 dark:text-red-300">pior</Badge>}
                            {f.zerou && <Badge variant="outline" className="ml-2">zerou</Badge>}
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{f.total}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="w-12 text-right text-sm tabular-nums">{f.pct.toLocaleString('pt-BR')}%</span>
                            <div className="relative h-2 flex-1 bg-muted rounded-full overflow-hidden">
                              <div className={`absolute left-0 top-0 h-full rounded-full ${pior ? 'bg-red-500' : 'bg-orange-400'}`}
                                style={{ width: `${(f.pct / maior) * 100}%` }} />
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {f.modal === null || f.modal === undefined ? '?' : f.modal}
                          {f.modal_padrao && (
                            <span className="ml-2 text-xs text-amber-700 dark:text-amber-400"
                              title="Fornecedor não achado pelo nome na planilha de fornecedores: o card usa 5 dias.">
                              padrão (não achado na planilha)
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {editando === f.fornecedor ? (
                            <form className="flex flex-wrap items-center gap-1"
                              onSubmit={(e) => { e.preventDefault(); salvar(f.fornecedor, valor); }}>
                              <input type="number" min="0" max="90" step="1" value={valor} autoFocus
                                aria-label={`Modal combinado de ${f.fornecedor}, em dias úteis`}
                                onChange={(e) => setValor(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Escape') setEditando(null); }}
                                className="h-7 w-16 rounded border border-input bg-background px-2 text-sm tabular-nums" />
                              <button type="submit" disabled={salvando || valor === ''}
                                className="h-7 rounded bg-primary px-2 text-xs text-primary-foreground disabled:opacity-50">Salvar</button>
                              {comb && (
                                <button type="button" disabled={salvando} onClick={() => salvar(f.fornecedor, '')}
                                  className="h-7 rounded border border-input px-2 text-xs hover:bg-muted disabled:opacity-50">Tirar</button>
                              )}
                              <button type="button" onClick={() => setEditando(null)}
                                className="h-7 rounded px-2 text-xs text-muted-foreground hover:bg-muted">Cancelar</button>
                              {erro && <span className="w-full text-xs text-red-600">{erro}</span>}
                            </form>
                          ) : (
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                              {comb ? (
                                <>
                                  <span className="tabular-nums font-medium">{comb.dias}</span>
                                  <span className="text-xs text-muted-foreground"
                                    title={comb.por ? `Registrado por ${comb.por}` : undefined}>desde {dia(comb.em)}</span>
                                </>
                              ) : <span className="text-muted-foreground">—</span>}
                              {f.alerta_modal && (
                                <Badge variant="outline" className="border-amber-400 text-amber-800 dark:text-amber-300"
                                  title={`A reunião combinou ${comb?.dias}; o ELO usa ${f.modal}. Corrigir na planilha de fornecedores.`}>
                                  falta na planilha
                                </Badge>
                              )}
                              {podeEditar && onSalvarModal && f.fornecedor !== '?' && (
                                <button type="button" onClick={() => editar(f)}
                                  aria-label={`Registrar o Modal combinado de ${f.fornecedor}`}
                                  className="h-6 rounded px-1.5 text-xs text-primary hover:bg-muted">
                                  {comb ? 'mudar' : 'preencher'}
                                </button>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{f.abertos}</TableCell>
                        {comAnterior && <TableCell className="text-right tabular-nums">{f.anterior}</TableCell>}
                      </TableRow>
                      {aberto && (
                        <TableRow className="bg-muted/30 hover:bg-muted/30">
                          <TableCell colSpan={colunas} className="py-2 pl-12">
                            <div className="max-h-72 overflow-y-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-xs text-muted-foreground">
                                    <th className="text-left font-medium py-1">Produto de {f.fornecedor}</th>
                                    <th className="text-right font-medium py-1 px-2">Chamados</th>
                                    <th className="text-right font-medium py-1 px-2">Ainda abertos</th>
                                    {comAnterior && <th className="text-right font-medium py-1 px-2">{periodo} dias antes</th>}
                                  </tr>
                                </thead>
                                <tbody>
                                  {produtos.map(p => (
                                    <tr key={`${p.sku_vtex || ''}|${p.produto}`} className="border-t border-border/50">
                                      <td className="py-1.5 pr-2">
                                        <span className="block">{p.produto}</span>
                                        <span className="text-xs text-muted-foreground font-mono">
                                          VTEX: {p.sku_vtex || '?'} · BSeller: {p.sku_bseller || '?'}
                                        </span>
                                      </td>
                                      <td className="text-right tabular-nums px-2">{p.total}</td>
                                      <td className="text-right tabular-nums px-2">{p.abertos}</td>
                                      {comAnterior && <td className="text-right tabular-nums px-2">{p.anterior}</td>}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-3">
          Conta os atendimentos que estão hoje na categoria Falha Compras, pela marca do pedido. Modal no ELO: o prazo do fornecedor que o card do Novo Atendimento mostra, da planilha de fornecedores. Modal combinado: o que a reunião combinou; é registro e não muda o ELO. Com o mesmo número de chamados, vem antes quem tem mais abertos. O produto é o do pedido no ELO, que guarda um item por entrega.
        </p>
      </CardContent>
    </Card>
  );
};

export default FalhaComprasFornecedor;
